import { SpinnerComponent } from "@zenith/components";
import {
  BannerContent,
  BannerService,
} from "@zenith/services/strapi/services/BannerService";
import React, { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface DashboardCarouselComponentProps {
  items?: BannerContent[];
}

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: {
    url: string;
    title: string;
  } | null;
}

const ImageModal: React.FC<ImageModalProps> = ({ isOpen, onClose, image }) => {
  if (!isOpen || !image) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div className="relative max-h-[90vh] max-w-[90vw]">
        <button
          className="absolute -top-10 right-0 p-2 text-white hover:text-neutral-300"
          onClick={onClose}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <img
          src={image.url}
          alt={image.title}
          className="max-h-[90vh] max-w-[90vw] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

const DashboardCarouselComponent = memo(function DashboardCarouselComponent(
  props: DashboardCarouselComponentProps
) {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<BannerContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [modalImage, setModalImage] = useState<{
    url: string;
    title: string;
  } | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const getSlideCount = (bannerCount: number): number => {
    if (isMobile) return bannerCount;
    return Math.ceil(bannerCount / 2);
  };

  useEffect(() => {
    const handleResize = () => {
      const wasDesktop = !isMobile;
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);

      if (wasDesktop !== newIsMobile && banners.length > 0) {
        const newSlideCount = newIsMobile
          ? banners.length
          : Math.ceil(banners.length / 2);
        const currentBannerIndex = wasDesktop ? currentSlide * 2 : currentSlide;
        const newSlideIndex = newIsMobile
          ? currentBannerIndex
          : Math.floor(currentBannerIndex / 2);
        setCurrentSlide(Math.min(newSlideIndex, newSlideCount - 1));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, banners.length, currentSlide]);

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const response = await BannerService.getMarketingBanners();
        if (response.success && response.data) {
          setBanners(response.data);
        } else {
          setError(
            response.message ||
              "Stay tuned for our latest offers and promotions!"
          );
        }
      } catch (err) {
        setError("Failed to load marketing banners");
        console.error("Error loading banners:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBanners();
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;

    const slideCount = getSlideCount(banners.length);
    if (slideCount <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideCount);
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length, isMobile]);

  const handleBannerClick = (banner: BannerContent) => {
    if (!banner.Image) return;

    if (banner.ctaLink) {
      // Handle both absolute URLs and relative paths
      if (banner.ctaLink.startsWith("http")) {
        window.open(banner.ctaLink, "_blank", "noopener,noreferrer");
      } else {
        navigate(banner.ctaLink);
      }
    } else {
      setModalImage({
        url: `${banner.Image[0].url}`,
        title: banner.Title,
      });
    }
  };

  if (loading) {
    return (
      <div
        className={`${
          !isMobile && "mt-8"
        } flex h-40 flex-col items-center justify-center rounded-lg bg-neutral-50`}
      >
        <SpinnerComponent type="primary" size={40} />
      </div>
    );
  }

  if (error || !banners.length) {
    return (
      <div className="flex h-40 flex-col items-center justify-center rounded-lg bg-neutral-50">
        <p className="text-lg font-bold text-neutral-900">Marketing Banner</p>
        <p className="text-xs text-neutral-600">
          {error || "There are no marketing banners to display."}
        </p>
      </div>
    );
  }

  const slideCount = getSlideCount(banners.length);

  const renderBanner = (banner: BannerContent, fullWidth = false) => (
    <div
      key={banner.id}
      className={`cursor-pointer ${
        fullWidth ? " w-full" : "w-full md:w-[calc(50%-0.5rem)]"
      }`}
      onClick={() => handleBannerClick(banner)}
    >
      {banner.Image && (
        <div className="relative h-full w-full overflow-hidden rounded-lg">
          <img
            src={`${banner.Image[0].url}`}
            alt={banner.Title}
            className="h-full w-full rounded-lg object-cover"
          />
          {banner.showText && (
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-black/50 to-transparent">
              <div className="flex h-full flex-col justify-center p-6">
                <h3 className="mb-2 text-xl font-bold text-white line-clamp-2">
                  {banner.Title}
                </h3>
                <p className="text-sm text-white/90 line-clamp-2">
                  {banner.Description}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="relative mx-auto mt-6 h-40 w-[calc(100%-1rem)] overflow-hidden rounded-lg">
        <div className="relative h-full overflow-hidden">
          <div
            className="flex h-full  transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {Array.from({ length: slideCount }).map((_, slideIndex) => {
              const startIdx = isMobile ? slideIndex : slideIndex * 2;
              const isLastSlide = slideIndex === slideCount - 1;
              const hasOneBannerInSlide =
                !isMobile && isLastSlide && banners.length % 2 === 1;

              return (
                <div key={slideIndex} className="w-full flex-shrink-0">
                  <div
                    className={`flex h-full w-full gap-4 
                      ${
                        hasOneBannerInSlide
                          ? "justify-center"
                          : "justify-between"
                      }
                    `}
                  >
                    {hasOneBannerInSlide ? (
                      renderBanner(banners[startIdx], true)
                    ) : (
                      <>
                        {renderBanner(banners[startIdx])}
                        {!isMobile &&
                          banners[startIdx + 1] &&
                          renderBanner(banners[startIdx + 1])}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {slideCount > 1 && (
        <div className="mt-2 flex justify-center gap-2 md:mt-4">
          {Array.from({ length: slideCount }).map((_, index) => (
            <button
              key={index}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-2 w-2 rounded-full transition-all ${
                index === currentSlide ? "w-4 bg-neutral-400" : "bg-neutral-300"
              }`}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </div>
      )}

      <ImageModal
        isOpen={!!modalImage}
        onClose={() => setModalImage(null)}
        image={modalImage}
      />
    </>
  );
});

export default DashboardCarouselComponent;
